import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Landmark, Star, X, ArrowDownToLine } from 'lucide-react';
import { tresorerieApi } from '../../services/tresorerieApi';
import type { CompteBancaire } from '../../types/tresorerie';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const ComptesBancaires: React.FC = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [depotCompte, setDepotCompte] = useState<CompteBancaire | null>(null);

  const { data: comptes = [], isLoading } = useQuery({
    queryKey: ['tresorerie', 'comptes'],
    queryFn: () => tresorerieApi.getComptes(),
  });

  const refresh = () => {
    queryClient.invalidateQueries(['tresorerie', 'comptes']);
    queryClient.invalidateQueries(['tresorerie', 'position']);
  };

  if (isLoading) return <div className="p-6 text-gray-500">Chargement des comptes…</div>;

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-3 py-2 rounded-lg">
          <Plus className="h-4 w-4" /> Nouveau compte
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {comptes.map((c) => (
          <div key={c.id} className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-lg bg-blue-50 text-blue-600"><Landmark className="h-5 w-5" /></span>
                <div>
                  <p className="font-semibold text-gray-900 flex items-center gap-1">
                    {c.nom}
                    {c.is_principal && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                  </p>
                  <p className="text-xs text-gray-400">{c.banque || c.type_compte_display}</p>
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400">Solde courant</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(num(c.solde_courant))}</p>
            </div>
            <button onClick={() => setDepotCompte(c)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg">
              <ArrowDownToLine className="h-4 w-4" /> Dépôt d'espèces
            </button>
          </div>
        ))}
        {comptes.length === 0 && (
          <div className="col-span-full bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
            Aucun compte. Créez-en un et marquez-le « principal » pour l'alimentation automatique.
          </div>
        )}
      </div>

      {showForm && <CompteForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
      {depotCompte && <DepotModal compte={depotCompte} onClose={() => setDepotCompte(null)} onSaved={() => { setDepotCompte(null); refresh(); }} />}
    </div>
  );
};

const CompteForm: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    nom: '', banque: '', numero_compte: '', type_compte: 'courant',
    solde_initial: '0', is_principal: false,
  });
  const mutation = useMutation(
    () => tresorerieApi.createCompte({ ...form, solde_initial: form.solde_initial } as any),
    { onSuccess: onSaved },
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Nouveau compte bancaire</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <input placeholder="Intitulé du compte" value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          <input placeholder="Banque" value={form.banque}
            onChange={(e) => setForm({ ...form, banque: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          <input placeholder="Numéro de compte" value={form.numero_compte}
            onChange={(e) => setForm({ ...form, numero_compte: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          <select value={form.type_compte} onChange={(e) => setForm({ ...form, type_compte: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
            <option value="courant">Compte courant</option>
            <option value="epargne">Compte épargne</option>
            <option value="mobile_money">Mobile Money</option>
          </select>
          <input type="number" placeholder="Solde initial" value={form.solde_initial}
            onChange={(e) => setForm({ ...form, solde_initial: e.target.value })}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_principal}
              onChange={(e) => setForm({ ...form, is_principal: e.target.checked })} />
            Compte principal (alimenté automatiquement par les flux non-espèces)
          </label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
          <button disabled={!form.nom || mutation.isLoading} onClick={() => mutation.mutate()}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
            {mutation.isLoading ? 'Enregistrement…' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DepotModal: React.FC<{ compte: CompteBancaire; onClose: () => void; onSaved: () => void }> = ({ compte, onClose, onSaved }) => {
  const [montant, setMontant] = useState('');
  const [reference, setReference] = useState('');
  const mutation = useMutation(
    () => tresorerieApi.depotEspeces(compte.id, Number(montant), reference),
    { onSuccess: onSaved },
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Dépôt d'espèces — {compte.nom}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-400">Crée un décaissement en caisse et un crédit sur ce compte.</p>
          <input type="number" placeholder="Montant déposé" value={montant}
            onChange={(e) => setMontant(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
          <input placeholder="Référence (optionnel)" value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
          <button disabled={Number(montant) <= 0 || mutation.isLoading} onClick={() => mutation.mutate()}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
            {mutation.isLoading ? 'Enregistrement…' : 'Déposer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComptesBancaires;
